import eyeSrc from '@/assets/images/eye_icon.svg'
import eyeSlashSrc from '@/assets/images/eye_slash_icon.svg'

import { Button, Flex, Form, Input, InputPassword } from '@/components'
import { Layout } from '@/components/layout'
import { Routes } from '@/configs'
import { useInput, useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { ConfirmResetPasswordRequest } from '@shared/models'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { PASSWORD_RULES, PasswordComplexity, PasswordMatch } from './PasswordRequirements.component'

export function ResetPassword() {
  const { navigate } = useRouting()
  const { sendAlert } = Alert.Hook()
  const { value: code, binder: bindCode, reset: resetCode } = useInput('')
  const { value: password, binder: bindPassword, reset: resetPassword } = useInput('')
  const { value: confirmPassword, binder: bindConfirmPassword, reset: resetConfirmPassword } = useInput('')
  const [showPassword, setShowPassword] = useState(false)

  const location = useLocation()
  const email = location.state?.email

  useEffect(() => {
    if (!email) {
      sendAlert({ type: 'error', title: 'Erro', message: 'Email não identificado. Inicie o processo novamente.' })

      navigate(Routes.SendResetPasswordCode.path)
    }
  }, [email, navigate, sendAlert])

  const { isRunning: isResetingPassword, run: changePassword } = Api.Command<void>(
    Api.Methods.POST,
    `auth/confirm-password-reset`,
  )

  const isPasswordValid = (pass: string) => PASSWORD_RULES.every((rule) => rule.test(pass))

  const isFormValid =
    code.trim().length > 0 && isPasswordValid(password) && password === confirmPassword && password.length > 0

  const onSubmit = () => {
    if (!code || !password || !confirmPassword) {
      alert('Preencha os campos para realizar a redefinição de senha!')
      return
    }

    if (!isPasswordValid(password)) {
      sendAlert({ type: 'error', title: 'Senha fraca', message: 'A senha não atende aos requisitos de segurança.' })
      return
    }

    if (password !== confirmPassword) {
      sendAlert({ type: 'error', title: 'Divergência', message: 'As senhas não coincidem.' })
      return
    }

    const input = {
      code,
      email,
      newPassword: password,
      confirmNewPassword: confirmPassword,
    } as ConfirmResetPasswordRequest

    changePassword<ConfirmResetPasswordRequest>({
      input,
      onSuccess: () => onSuccess('Senha redefinida com sucesso!'),
    })
  }

  const onSuccess = (message: string) => {
    sendAlert({
      type: 'success',
      title: 'Sucesso',
      message,
    })

    resetCode()
    resetPassword()
    resetConfirmPassword()

    navigate(Routes.Login.path)
  }

  return (
    <Layout.Login>
      <Layout.Padder>
        <Flex $type="row" $justify="center" $align="center" $gap={24} $fit>
          <Form title="Confirme a redefinição de senha" onSubmit={onSubmit}>
            <Flex $type="column" $gap={24} style={{ minWidth: '400px' }}>
              <p>Digite o código de confirmação recebido por email e sua nova senha.</p>
              <Input label="Código" placeholder="Digite seu código" {...bindCode} required />
              <InputPassword
                label="Nova senha"
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua nova senha"
                {...bindPassword}
                required
                iconPassword={showPassword ? eyeSlashSrc : eyeSrc}
                onIconClick={() => setShowPassword(!showPassword)}
              />
              <PasswordComplexity value={password} />
              <InputPassword
                label="Confirme a nova senha"
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirme sua nova senha"
                {...bindConfirmPassword}
                required
                iconPassword={showPassword ? eyeSlashSrc : eyeSrc}
                onIconClick={() => setShowPassword(!showPassword)}
              />
              <PasswordMatch value={confirmPassword} target={password} />
            </Flex>
            <Flex $type="column" $gap={12} style={{ minWidth: '400px' }}>
              <Button.Root
                $variant="primary"
                style={{ width: '100%' }}
                type="submit"
                $loading={isResetingPassword}
                disabled={!isFormValid || isResetingPassword}>
                <Button.Text value="Confirme a redefinição" />
              </Button.Root>
              <Button.Root
                $variant="link"
                style={{ width: '100%' }}
                type="button"
                onClick={() => navigate(Routes.SendResetPasswordCode.path)}>
                <Button.Text value="Voltar" />
              </Button.Root>
            </Flex>
          </Form>
        </Flex>
      </Layout.Padder>
    </Layout.Login>
  )
}
